import { HubShell } from "@/components/layouts/HubLayout";
import { StatusDisplay } from "@/components/overlays/StatusDisplay";
import { useGetLargeSiteQuery, useGetPortfolioQuery } from "@/store/services/supabase";
import { PortfolioPasswordInput } from "@/tools/aggregate/portfolio-export/components/PortfolioPasswordInput";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { useAvailablePortfolioTypes } from "@/utils/hooks/useAvailablePortfolioTypes";
import { useKeys } from "@/utils/hooks/useKeys";
import { useRequireSession } from "@/utils/hooks/useRequireSession";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CollectionProvider } from "../context/CollectionContext";
import { useAnalysisViewRoute } from "../hooks/useAnalysisViewRoute";
import type { CollectionMode } from "../types";
import { hasCollectionKey, toCollectionSchema } from "../utils/toCollectionSchema";

// Only the collection UI is split out, so the gates below are decided before
// the heavy chunk is fetched.
const CollectionPage = dynamic(() => import("./CollectionPage").then(data => data.CollectionPage), {
  ssr: false
});

/** The shared body of `/portfolios/[id]` and `/large-sites/[id]`. */
export const CollectionRoute = ({
  mode
}: {
  mode: CollectionMode;
}) => {
  const {
    query
  } = useRouter();
  const id = typeof query.id === "object" ? query.id[0] : query.id;
  const isLargeSite = mode === "large_site";

  // The two routes differ only in wording and which table the record lives in.
  const {
    t
  } = useTranslation("pages", {
    keyPrefix: "collection"
  });
  const tCollection = isLargeSite ? "largeSite" : "portfolio";

  // Hooks can't be called conditionally, so both queries are mounted and the
  // one this route doesn't need is skipped.
  const portfolio = useGetPortfolioQuery(id, {
    skip: isLargeSite
  });
  const largeSite = useGetLargeSiteQuery(id, {
    skip: !isLargeSite
  });
  const {
    data,
    isError
  } = isLargeSite ? largeSite : portfolio;
  const session = useRequireSession();
  const [keyPair] = useKeys();
  const [schema, setSchema] = useState<PortfolioExportJobSchema>();
  const [schemaError, setSchemaError] = useState(false);
  const availablePortfolioTypes = useAvailablePortfolioTypes();
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite
  });
  const collectionRoute = useAnalysisViewRoute({
    id,
    isLargeSite,
    canRunAutoAdaptation
  });
  useEffect(() => {
    getSchema();
  }, [id, keyPair, data]);
  const getSchema = async () => {
    if (!id || !keyPair || keyPair.isLoading || !data) {
      return;
    }
    if (!hasCollectionKey(data, keyPair)) {
      return;
    }
    try {
      setSchema(await toCollectionSchema(data, isLargeSite, keyPair, t(`${tCollection}.untitled`)));
      setSchemaError(false);
    } catch (err) {
      console.error(err);
      setSchemaError(true);
    }
  };
  if (!session) {
    return <HubShell disableCollapse>
        <StatusDisplay status="loading" width={140} title={<div className="loading">{t("loading")}</div>} />
      </HubShell>;
  }
  if (!keyPair.user) {
    return <HubShell disableCollapse>
        <PortfolioPasswordInput prompt={t(`${tCollection}.unlockPrompt`)} isLoading={keyPair.isLoading} />
      </HubShell>;
  }
  if (isError || schemaError) {
    return <HubShell disableCollapse>
        <StatusDisplay status="error" title={t(`${tCollection}.errorTitle`)} description={t("errorDescription")} />
      </HubShell>;
  }

  // Unlocked, but not with the key this one was encrypted against
  if (data && !data.is_private && !keyPair.org) {
    return <HubShell disableCollapse>
        <StatusDisplay status="warning" title={t(`${tCollection}.noAccessTitle`)} description={t(`${tCollection}.noAccessDescription`)} />
      </HubShell>;
  }
  if (!schema) {
    return <HubShell disableCollapse>
        <StatusDisplay status="loading" width={140} title={<div className="loading">{t("loading")}</div>} />
      </HubShell>;
  }
  return <CollectionProvider {...collectionRoute} data-sentry-element="CollectionProvider" data-sentry-component="CollectionRoute" data-sentry-source-file="CollectionRoute.tsx">
      <CollectionPage mode={mode} schema={schema} availablePortfolioTypes={isLargeSite ? undefined : availablePortfolioTypes} data-sentry-element="CollectionPage" data-sentry-source-file="CollectionRoute.tsx" />
    </CollectionProvider>;
};